import fp from '@crazyegginc/fingerprint';

import { msToHumanDuration } from '/src/utils/date';
import { inflect } from '/src/utils/string';
import { getVisitorDisplayName } from '/src/utils/visitor';

import { BASIC_VISITOR, LIVE_EVENT_TYPES } from './constants';
import { getPageName } from './live-functions';

function flattenText(parts) {
  return parts.flatMap((part) => {
    if (Array.isArray(part)) {
      return flattenText(part);
    }

    if (part === null || part === undefined || part === '') {
      return [];
    }

    return [String(part)];
  });
}

function joinText(...parts) {
  return flattenText(parts).join(' ');
}

function getVisitSource(referrer, utm) {
  if (!referrer && !utm) {
    return 'direct visit';
  }

  if (referrer && !utm) {
    return referrer;
  }

  if (!utm) {
    return '';
  }

  let names = '';
  let values = '';

  for (const param of ['source', 'medium', 'campaign', 'term', 'content']) {
    if (utm[param] && (names.match(/ \+ /g) || []).length < 2) {
      names += ` + UTM ${param}`;
      values += ` + ${utm[param]}`;
    }
  }

  let source = `${names.replace(/^ + /, '')}: ${values.replace(/^ + /, '')}`;

  if (referrer) {
    source += ` on ${referrer}`;
  }

  return source;
}

function getVisitorText(liveEvent, identifyEvents) {
  const visitorId = liveEvent.data?.visitor_id;

  if (!visitorId) {
    return '';
  }

  return getVisitorDisplayName(identifyEvents[visitorId], visitorId);
}

function getClickTargetName(fingerprint = {}) {
  return fingerprint.name || Object.keys(fp.TAG_TYPES).find((tag) => fp.TAG_TYPES[tag] === fingerprint.type);
}

export function getEventCardSearchText({ liveEvent, identifyEvents, visitEvents, isImpersonated }) {
  const attrs = liveEvent.data?.attributes ?? {};
  const visitorText = getVisitorText(liveEvent, identifyEvents);

  switch (liveEvent.event_type) {
    case LIVE_EVENT_TYPES.VISIT: {
      const page = getPageName(attrs.url);
      const source = getVisitSource(attrs.referrer, attrs.utm);

      return joinText(
        visitorText,
        `visited ${page === 'homepage' ? '' : 'page'}`,
        page,
        attrs.country?.name,
        attrs.country && source ? `via ${source}` : '',
      );
    }
    case LIVE_EVENT_TYPES.DAILY_VISIT: {
      const page = getPageName(attrs.path);

      return joinText(
        BASIC_VISITOR,
        `visited ${page === 'homepage' ? '' : 'page'}`,
        page,
        attrs.country?.name,
        attrs.referrer && `via ${attrs.referrer}`,
        isImpersonated && liveEvent.data?.visitor_id,
      );
    }
    case LIVE_EVENT_TYPES.DAILY_VISIT_COUNTER:
      return joinText(attrs.count, 'additional Basic', inflect('Visitor', attrs.count), 'visited');
    case LIVE_EVENT_TYPES.CLICK: {
      const clickType = attrs.rage ? 'rage' : attrs.dead ? 'dead' : '';
      const url = visitEvents[attrs.visit_id]?.data?.attributes?.url;

      return joinText(
        visitorText,
        clickType,
        'clicked',
        getClickTargetName(attrs.fingerprint),
        url && `on ${getPageName(url)}`,
      );
    }
    case LIVE_EVENT_TYPES.ERROR:
      return joinText(visitorText, 'received error', attrs.fingerprint, attrs.url && `on ${getPageName(attrs.url)}`);
    case LIVE_EVENT_TYPES.TAG:
      return joinText(visitorText, 'got tagged with', attrs.tags);
    case LIVE_EVENT_TYPES.ECOMMERCE:
      if (attrs.sub_event_type === 1) {
        return joinText(
          visitorText,
          'added',
          attrs.quantity && attrs.product_name ? [attrs.quantity, 'x', attrs.product_name] : 'something',
          'to cart',
          attrs.price && `${attrs.price} ${attrs.currency}`,
        );
      }

      if (attrs.sub_event_type === 3) {
        return joinText(
          visitorText,
          'completed checkout',
          attrs.total && `for ${attrs.total}`,
          attrs.order_id && `Order ID #${attrs.order_id}`,
        );
      }

      return visitorText;
    case LIVE_EVENT_TYPES.FORM: {
      if (attrs.sub_event_type === 4) {
        return joinText(visitorText, 'signed up!');
      }

      if (attrs.sub_event_type === 5) {
        return joinText(visitorText, 'logged in!');
      }

      const form = (typeof attrs.form_name === 'string' ? attrs.form_name : '') || attrs.form_id;
      let action = '';

      if (attrs.sub_event_type === 1) action = 'completed';
      else if (attrs.sub_event_type === 2) action = 'resubmitted';
      else if (attrs.sub_event_type === 3) action = 'abandoned';

      return joinText(
        visitorText,
        `${action} ${form ? '' : 'a'} form`,
        form,
        attrs.form_action && `to ${attrs.form_action}`,
        attrs.time_spent && `after ${msToHumanDuration(attrs.time_spent)}`,
      );
    }
    case LIVE_EVENT_TYPES.SURVEY_RESPONSE:
      return joinText(visitorText, 'responded to survey');
    case LIVE_EVENT_TYPES.GOAL_CONVERSION:
      return joinText(
        visitorText,
        'completed',
        attrs.goal_name,
        'Conversion',
        attrs.unique ? 'for the first time' : 'again',
        attrs.addon_name && `via ${attrs.addon_name}`,
      );
    default:
      return visitorText;
  }
}
